import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { METERS_TO_FEET, METERS_TO_YARDS, METERS_TO_MILES, formatDistance as formatDistanceUtil, formatDistancePrecise } from '../../utils/units';
import { SLOPE_BANDS, slopeToColor, aspectBandsFor, aspectToColor, bearingShort, GEOMORPHON_CLASSES, geomorphonToColor, geomorphonLabel, TWI_LOW_RGB, TWI_HIGH_RGB, twiToColor, flowAccumulationToColor, rgbHex, } from './elevationColors';
// Honor the user's full distance-unit pick across the whole chart.
// Earlier this collapsed km→m and yd/mi→ft for "natural elevation
// readings," but the result was a confusing modal where the y-axis
// silently stuck in m/ft no matter what the user picked. Now the
// chart converts elevation to whichever unit was selected; precision
// is sized for typical elevation magnitudes per unit.
const convertElevation = (meters, unit) => {
    switch (unit) {
        case 'km': return { value: meters / 1000, precision: 3, suffix: 'km' };
        case 'ft': return { value: meters * METERS_TO_FEET, precision: 0, suffix: 'ft' };
        case 'yd': return { value: meters * METERS_TO_YARDS, precision: 0, suffix: 'yd' };
        case 'mi': return { value: meters * METERS_TO_MILES, precision: 4, suffix: 'mi' };
        case 'm':
        default: return { value: meters, precision: 0, suffix: 'm' };
    }
};
const formatElevation = (meters, unit) => {
    const { value, precision, suffix } = convertElevation(meters, unit);
    return `${value.toFixed(precision)} ${suffix}`;
};
const formatDistance = formatDistanceUtil;
export const ElevationProfile = ({ elevationProfile, unit, color, onUnitChange, segmentBoundaries, highlightSegmentRange, onHoverFraction, externalCrosshairFraction, hideMeasurementSection = false, colorBy = 'elevation', }) => {
    var _a, _b;
    // A path edit clears the profile to `undefined` while the recalculation is
    // in flight. Hold the last loaded profile and keep rendering it so the
    // chart, legends, and stats stay mounted (no unmount/remount flash) — the
    // curve simply swaps in place when fresh data lands. `null` (definitively
    // no data) still clears immediately.
    const lastLoadedProfileRef = useRef(undefined);
    if (elevationProfile !== undefined) {
        lastLoadedProfileRef.current = elevationProfile;
    }
    const profile = elevationProfile !== undefined ? elevationProfile : lastLoadedProfileRef.current;
    const isRecalculating = elevationProfile === undefined && !!profile;
    const hemisphere = (_a = profile === null || profile === void 0 ? void 0 : profile.hemisphere) !== null && _a !== void 0 ? _a : 'N';
    const aspectBands = useMemo(() => aspectBandsFor(hemisphere), [hemisphere]);
    // Collapsible "Statistics" block — mirrors the Segment Details affordance so
    // the dialog stays compact by default. Stats are click-to-expand.
    const [showStats, setShowStats] = useState(false);
    const [internalHoverFraction, setInternalHoverFraction] = useState(null);
    const svgRef = useRef(null);
    const containerRef = useRef(null);
    // Width is responsive (tracks container via ResizeObserver). Height stays fixed for now;
    // can be wired the same way later if we want vertical resize too.
    const [containerWidth, setContainerWidth] = useState(280);
    useEffect(() => {
        const el = containerRef.current;
        if (!el || typeof ResizeObserver === 'undefined')
            return;
        const ro = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry)
                return;
            const w = Math.max(120, Math.round(entry.contentRect.width));
            setContainerWidth((prev) => (prev === w ? prev : w));
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    const allSamples = useMemo(() => {
        if (!profile)
            return [];
        return profile.samples.map(s => ({
            distance: s.distanceMeters,
            elevation: s.valid ? s.elevationMeters : null,
        }));
    }, [profile]);
    const validSamples = useMemo(() => {
        return allSamples.filter((s) => s.elevation !== null);
    }, [allSamples]);
    const totalDistance = (profile === null || profile === void 0 ? void 0 : profile.totalDistanceMeters) || 0;
    const totalDistance3d = (profile === null || profile === void 0 ? void 0 : profile.totalDistance3dMeters) || 0;
    // Per-sample slope (degrees) and aspect (degrees, 0-360) read straight off the
    // backend response. Sourced from raw COG sampling so values match the on-map
    // raster layers exactly. Indices align with elevationProfile.samples / allSamples.
    const sampleSlopesDeg = useMemo(() => {
        if (!profile)
            return [];
        return profile.samples.map(s => {
            var _a;
            const v = (_a = s.layerValues) === null || _a === void 0 ? void 0 : _a.slope;
            return v && v.valid ? v.value : null;
        });
    }, [profile]);
    const sampleAspectsDeg = useMemo(() => {
        if (!profile)
            return [];
        return profile.samples.map(s => {
            var _a;
            const v = (_a = s.layerValues) === null || _a === void 0 ? void 0 : _a.aspect;
            return v && v.valid ? v.value : null;
        });
    }, [profile]);
    const sampleGeomorphons = useMemo(() => {
        if (!profile)
            return [];
        return profile.samples.map(s => {
            var _a;
            const v = (_a = s.layerValues) === null || _a === void 0 ? void 0 : _a.geomorphons;
            return v && v.valid ? v.value : null;
        });
    }, [profile]);
    const sampleTWIs = useMemo(() => {
        if (!profile)
            return [];
        return profile.samples.map(s => {
            var _a;
            const v = (_a = s.layerValues) === null || _a === void 0 ? void 0 : _a.twi;
            return v && v.valid ? v.value : null;
        });
    }, [profile]);
    const sampleFlowAccs = useMemo(() => {
        if (!profile)
            return [];
        return profile.samples.map(s => {
            var _a;
            const v = (_a = s.layerValues) === null || _a === void 0 ? void 0 : _a.flow_accumulation;
            return v && v.valid ? v.value : null;
        });
    }, [profile]);
    // Path-aligned grade per sample (degrees) — central finite difference over
    // neighbouring elevation samples. Used by the slope_grade colour-by mode
    // and the slope_grade tooltip. This is the answer to "how steep is the
    // path itself if I walk along it" — distinct from terrain slope at this
    // point (which is what `sampleSlopesDeg` reads from the slope raster).
    const samplePathGrades = useMemo(() => {
        if (!profile)
            return [];
        const samples = profile.samples;
        const n = samples.length;
        if (n < 2)
            return new Array(n).fill(null);
        const out = new Array(n).fill(null);
        for (let i = 0; i < n; i++) {
            const lo = i === 0 ? 0 : i - 1;
            const hi = i === n - 1 ? n - 1 : i + 1;
            const a = samples[lo];
            const b = samples[hi];
            if (!a.valid || !b.valid)
                continue;
            const dh = b.distanceMeters - a.distanceMeters;
            if (dh <= 0)
                continue;
            const dz = b.elevationMeters - a.elevationMeters;
            out[i] = Math.atan2(dz, dh) * (180 / Math.PI);
        }
        return out;
    }, [profile]);
    // Raster-wide TWI min/max (sent by the backend in layer_stats). This is the
    // SAME range used to colourise the on-map TWI raster, so chart colours align
    // pixel-for-pixel with what the user sees beneath the path. Falls back to a
    // path-scoped range only if the backend didn't send stats (legacy projects /
    // missing raster), so the chart still renders something useful.
    const twiRange = useMemo(() => {
        var _a;
        const fromStats = (_a = profile === null || profile === void 0 ? void 0 : profile.layerStats) === null || _a === void 0 ? void 0 : _a.twi;
        if (fromStats && Number.isFinite(fromStats.min) && Number.isFinite(fromStats.max) && fromStats.max > fromStats.min) {
            return { min: fromStats.min, max: fromStats.max };
        }
        let min = Infinity;
        let max = -Infinity;
        for (const v of sampleTWIs) {
            if (v === null)
                continue;
            if (v < min)
                min = v;
            if (v > max)
                max = v;
        }
        return Number.isFinite(min) && Number.isFinite(max) ? { min, max } : null;
    }, [profile, sampleTWIs]);
    // Raster-wide flow-accumulation min/max — same shape as TWI's range, drives
    // the log-scaled Blues colormap. Backend sends the raw (un-logged) min/max
    // and the colour helper handles log1p internally.
    const flowAccRange = useMemo(() => {
        var _a;
        const fromStats = (_a = profile === null || profile === void 0 ? void 0 : profile.layerStats) === null || _a === void 0 ? void 0 : _a.flow_accumulation;
        if (fromStats && Number.isFinite(fromStats.min) && Number.isFinite(fromStats.max) && fromStats.max > fromStats.min) {
            return { min: fromStats.min, max: fromStats.max };
        }
        let min = Infinity;
        let max = -Infinity;
        for (const v of sampleFlowAccs) {
            if (v === null)
                continue;
            if (v < min)
                min = v;
            if (v > max)
                max = v;
        }
        return Number.isFinite(min) && Number.isFinite(max) ? { min, max } : null;
    }, [profile, sampleFlowAccs]);
    // Per-sample value gradient stops for the active colorBy mode.
    // Each stop's offset is the sample's distance fraction along the chart's x-axis.
    // Geomorphons is categorical so adjacent samples in different classes will blend
    // briefly across one inter-sample distance — visually negligible at ~1m sampling.
    const valueGradientStops = useMemo(() => {
        if (totalDistance <= 0)
            return [];
        const stops = [];
        for (let i = 0; i < allSamples.length; i++) {
            let color = null;
            if (colorBy === 'slope_grade') {
                const grade = samplePathGrades[i];
                if (grade !== null)
                    color = slopeToColor(grade);
            }
            else if (colorBy === 'slope') {
                const slope = sampleSlopesDeg[i];
                if (slope !== null)
                    color = slopeToColor(slope);
            }
            else if (colorBy === 'aspect') {
                const aspect = sampleAspectsDeg[i];
                if (aspect !== null)
                    color = aspectToColor(aspect, hemisphere);
            }
            else if (colorBy === 'geomorphons') {
                const g = sampleGeomorphons[i];
                if (g !== null)
                    color = geomorphonToColor(g);
            }
            else if (colorBy === 'twi') {
                const v = sampleTWIs[i];
                if (v !== null && twiRange)
                    color = twiToColor(v, twiRange.min, twiRange.max);
            }
            else if (colorBy === 'flow_accumulation') {
                const v = sampleFlowAccs[i];
                if (v !== null && flowAccRange)
                    color = flowAccumulationToColor(v, flowAccRange.min, flowAccRange.max);
            }
            if (color === null)
                continue;
            const offset = allSamples[i].distance / totalDistance;
            stops.push({ offset, color });
        }
        return stops;
    }, [allSamples, samplePathGrades, sampleSlopesDeg, sampleAspectsDeg, sampleGeomorphons, sampleTWIs, sampleFlowAccs, twiRange, flowAccRange, hemisphere, totalDistance, colorBy]);
    const valueGradientId = useMemo(() => `valueGradient-${Math.random().toString(36).slice(2, 9)}`, []);
    const showValueGradient = (colorBy === 'slope_grade' || colorBy === 'slope' || colorBy === 'aspect' || colorBy === 'geomorphons' || colorBy === 'twi' || colorBy === 'flow_accumulation') && valueGradientStops.length > 0;
    const { minElev, maxElev, elevRange } = useMemo(() => {
        if (validSamples.length === 0)
            return { minElev: 0, maxElev: 100, elevRange: 100 };
        const elevations = validSamples.map(s => s.elevation);
        const min = Math.min(...elevations);
        const max = Math.max(...elevations);
        const range = max - min;
        const padding = Math.max(range * 0.1, 5);
        return {
            minElev: min - padding,
            maxElev: max + padding,
            elevRange: range + padding * 2
        };
    }, [validSamples]);
    const smoothedSamples = useMemo(() => {
        if (allSamples.length < 3)
            return allSamples;
        const smoothed = [];
        for (let i = 0; i < allSamples.length; i++) {
            const curr = allSamples[i];
            if (curr.elevation === null) {
                smoothed.push(curr);
                continue;
            }
            if (i === 0 || i === allSamples.length - 1) {
                smoothed.push(curr);
                continue;
            }
            const prev = allSamples[i - 1];
            const next = allSamples[i + 1];
            if (prev.elevation === null || next.elevation === null) {
                smoothed.push(curr);
            }
            else {
                const smoothedElev = prev.elevation * 0.25 + curr.elevation * 0.5 + next.elevation * 0.25;
                smoothed.push({ distance: curr.distance, elevation: smoothedElev });
            }
        }
        return smoothed;
    }, [allSamples]);
    const stats = useMemo(() => {
        if (validSamples.length === 0)
            return null;
        const elevations = validSamples.map(s => s.elevation);
        const n = elevations.length;
        const min = Math.min(...elevations);
        const max = Math.max(...elevations);
        const range = max - min;
        const mean = elevations.reduce((a, b) => a + b, 0) / n;
        const squaredDiffs = elevations.map(e => Math.pow(e - mean, 2));
        const avgSquaredDiff = squaredDiffs.reduce((a, b) => a + b, 0) / n;
        const stdev = Math.sqrt(avgSquaredDiff);
        let gain = 0;
        let loss = 0;
        for (let i = 1; i < validSamples.length; i++) {
            const diff = validSamples[i].elevation - validSamples[i - 1].elevation;
            if (diff > 0)
                gain += diff;
            else
                loss += Math.abs(diff);
        }
        // Net elevation change: end minus start (signed). Telescopes to gain - loss,
        // and is the figure that matches a straight contour-to-contour difference —
        // shown beside Gain/Loss so the cumulative-vs-net distinction is explicit.
        const net = elevations[n - 1] - elevations[0];
        return { min, max, range, mean, stdev, gain, loss, net };
    }, [validSamples]);
    const svgWidth = containerWidth;
    const svgHeight = 120; // bumped from 100 to leave room for X-axis labels rendered in-SVG
    const paddingLeft = 45;
    const paddingRight = 8;
    const paddingTop = 5;
    const paddingBottom = 18; // extra room for in-SVG X-axis labels
    const graphWidth = svgWidth - paddingLeft - paddingRight;
    const graphHeight = svgHeight - paddingTop - paddingBottom;
    const yAxisTicks = useMemo(() => {
        const ticks = [];
        const numTicks = 3;
        for (let i = 0; i < numTicks; i++) {
            const fraction = i / (numTicks - 1);
            const elevMeters = maxElev - fraction * elevRange;
            const y = paddingTop + fraction * graphHeight;
            const { value, precision, suffix } = convertElevation(elevMeters, unit);
            ticks.push({
                value: elevMeters,
                y,
                label: value.toFixed(precision),
                suffix,
            });
        }
        return ticks;
    }, [minElev, maxElev, elevRange, graphHeight, unit]);
    const { segmentPaths, segmentAreaPaths } = useMemo(() => {
        if (totalDistance === 0)
            return { segmentPaths: [], segmentAreaPaths: [] };
        const baseline = paddingTop + graphHeight;
        const paths = [];
        const areaPaths = [];
        let runStart = -1;
        for (let i = 0; i <= smoothedSamples.length; i++) {
            const isValid = i < smoothedSamples.length && smoothedSamples[i].elevation !== null;
            if (isValid && runStart === -1) {
                runStart = i;
            }
            else if (!isValid && runStart !== -1) {
                const run = smoothedSamples.slice(runStart, i);
                if (run.length >= 2) {
                    const points = run.map(s => ({
                        x: paddingLeft + (s.distance / totalDistance) * graphWidth,
                        y: paddingTop + graphHeight - ((s.elevation - minElev) / elevRange) * graphHeight,
                    }));
                    let path = `M ${points[0].x} ${points[0].y}`;
                    for (let j = 1; j < points.length; j++) {
                        const prev = points[j - 1];
                        const curr = points[j];
                        const midX = (prev.x + curr.x) / 2;
                        path += ` Q ${prev.x} ${prev.y} ${midX} ${(prev.y + curr.y) / 2}`;
                    }
                    const last = points[points.length - 1];
                    path += ` L ${last.x} ${last.y}`;
                    paths.push(path);
                    const areaPath = `${path} L ${last.x} ${baseline} L ${points[0].x} ${baseline} Z`;
                    areaPaths.push(areaPath);
                }
                runStart = -1;
            }
        }
        return { segmentPaths: paths, segmentAreaPaths: areaPaths };
    }, [smoothedSamples, totalDistance, graphWidth, graphHeight, minElev, elevRange, paddingLeft, paddingTop]);
    const endpointDots = useMemo(() => {
        if (totalDistance === 0 || smoothedSamples.length === 0)
            return [];
        const dots = [];
        for (let i = 0; i < smoothedSamples.length; i++) {
            const s = smoothedSamples[i];
            if (s.elevation === null)
                continue;
            const prevNull = i === 0 || smoothedSamples[i - 1].elevation === null;
            const nextNull = i === smoothedSamples.length - 1 || smoothedSamples[i + 1].elevation === null;
            if (prevNull || nextNull) {
                dots.push({
                    cx: paddingLeft + (s.distance / totalDistance) * graphWidth,
                    cy: paddingTop + graphHeight - ((s.elevation - minElev) / elevRange) * graphHeight,
                });
            }
        }
        return dots;
    }, [smoothedSamples, totalDistance, graphWidth, graphHeight, minElev, elevRange, paddingLeft, paddingTop]);
    const activeFraction = (_b = internalHoverFraction !== null && internalHoverFraction !== void 0 ? internalHoverFraction : externalCrosshairFraction) !== null && _b !== void 0 ? _b : null;
    const crosshairInfo = useMemo(() => {
        var _a, _b, _c, _d, _e, _f;
        if (activeFraction === null || allSamples.length < 2 || totalDistance === 0)
            return null;
        const targetDistance = activeFraction * totalDistance;
        const x = paddingLeft + activeFraction * graphWidth;
        // Find the closest sample index for value lookups.
        const findClosestIdx = (dist) => {
            let bestIdx = -1;
            let bestDelta = Infinity;
            for (let i = 0; i < allSamples.length; i++) {
                const d = Math.abs(allSamples[i].distance - dist);
                if (d < bestDelta) {
                    bestDelta = d;
                    bestIdx = i;
                }
            }
            return bestIdx;
        };
        const closestIdx = findClosestIdx(targetDistance);
        const slopeDeg = closestIdx >= 0
            ? (colorBy === 'slope_grade' ? (_a = samplePathGrades[closestIdx]) !== null && _a !== void 0 ? _a : null : (_b = sampleSlopesDeg[closestIdx]) !== null && _b !== void 0 ? _b : null)
            : null;
        const bearingDeg = closestIdx >= 0 ? (_c = sampleAspectsDeg[closestIdx]) !== null && _c !== void 0 ? _c : null : null;
        const geomorphonClass = closestIdx >= 0 ? (_d = sampleGeomorphons[closestIdx]) !== null && _d !== void 0 ? _d : null : null;
        const twiValue = closestIdx >= 0 ? (_e = sampleTWIs[closestIdx]) !== null && _e !== void 0 ? _e : null : null;
        const flowAccValue = closestIdx >= 0 ? (_f = sampleFlowAccs[closestIdx]) !== null && _f !== void 0 ? _f : null : null;
        for (let i = 1; i < smoothedSamples.length; i++) {
            if (smoothedSamples[i].distance >= targetDistance || i === smoothedSamples.length - 1) {
                const prev = smoothedSamples[i - 1];
                const curr = smoothedSamples[i];
                if (prev.elevation === null || curr.elevation === null) {
                    return { x, y: null, elevation: null, distance: targetDistance, slopeDeg, bearingDeg, geomorphonClass, twiValue, flowAccValue };
                }
                const segLen = curr.distance - prev.distance;
                const t = segLen > 0 ? (targetDistance - prev.distance) / segLen : 0;
                const elevation = prev.elevation + t * (curr.elevation - prev.elevation);
                const y = paddingTop + graphHeight - ((elevation - minElev) / elevRange) * graphHeight;
                return { x, y, elevation, distance: targetDistance, slopeDeg, bearingDeg, geomorphonClass, twiValue, flowAccValue };
            }
        }
        return null;
    }, [activeFraction, smoothedSamples, allSamples, totalDistance, graphWidth, graphHeight, paddingLeft, paddingTop, minElev, elevRange, samplePathGrades, sampleSlopesDeg, sampleAspectsDeg, sampleGeomorphons, sampleTWIs, sampleFlowAccs, colorBy]);
    const handleSvgMouseMove = useCallback((e) => {
        const svg = svgRef.current;
        if (!svg)
            return;
        const rect = svg.getBoundingClientRect();
        const scaleX = svgWidth / rect.width;
        const mouseX = (e.clientX - rect.left) * scaleX;
        const fraction = Math.max(0, Math.min(1, (mouseX - paddingLeft) / graphWidth));
        setInternalHoverFraction(fraction);
        onHoverFraction === null || onHoverFraction === void 0 ? void 0 : onHoverFraction(fraction);
    }, [graphWidth, paddingLeft, svgWidth, onHoverFraction]);
    const handleSvgMouseLeave = useCallback(() => {
        setInternalHoverFraction(null);
        onHoverFraction === null || onHoverFraction === void 0 ? void 0 : onHoverFraction(null);
    }, [onHoverFraction]);
    const placeholderText = profile === null ? 'No elevation data available' :
        profile === undefined ? 'Calculating profile…' :
            (allSamples.length < 2 || validSamples.length < 2 || !stats) ? 'Elevation data unavailable' :
                null;
    const isPlaceholder = placeholderText !== null;
    return (_jsxs("div", { children: [_jsxs("div", { ref: containerRef, className: "rounded overflow-hidden mb-2", style: { backgroundColor: 'var(--forest)', height: svgHeight, position: 'relative' }, children: [isRecalculating && (_jsx("div", { className: "text-[9px]", style: { position: 'absolute', top: 3, right: 8, color: 'var(--dew)', opacity: 0.6, pointerEvents: 'none' }, children: "Updating\u2026" })), isPlaceholder ? (_jsx("div", { className: "w-full h-full flex items-center justify-center text-xs", style: { color: 'var(--dew)' }, children: placeholderText })) : (_jsxs("svg", { ref: svgRef, width: svgWidth, height: svgHeight, viewBox: `0 0 ${svgWidth} ${svgHeight}`, onMouseMove: handleSvgMouseMove, onMouseLeave: handleSvgMouseLeave, style: { cursor: onHoverFraction ? 'crosshair' : undefined, display: 'block' }, children: [_jsxs("defs", { children: [_jsxs("linearGradient", { id: `profileGradient-${color.replace('#', '')}`, x1: "0%", y1: "0%", x2: "0%", y2: "100%", children: [_jsx("stop", { offset: "0%", stopColor: color, stopOpacity: "0.6" }), _jsx("stop", { offset: "100%", stopColor: color, stopOpacity: "0.1" })] }), showValueGradient && (_jsx("linearGradient", { id: valueGradientId, gradientUnits: "userSpaceOnUse", x1: paddingLeft, y1: 0, x2: paddingLeft + graphWidth, y2: 0, children: valueGradientStops.map((s, i) => (_jsx("stop", { offset: s.offset, stopColor: s.color }, i))) })), showValueGradient && (_jsx("linearGradient", { id: `${valueGradientId}-fill`, gradientUnits: "userSpaceOnUse", x1: paddingLeft, y1: 0, x2: paddingLeft + graphWidth, y2: 0, children: valueGradientStops.map((s, i) => (_jsx("stop", { offset: s.offset, stopColor: s.color, stopOpacity: "0.35" }, i))) }))] }), _jsx("line", { x1: paddingLeft, y1: paddingTop, x2: paddingLeft, y2: paddingTop + graphHeight, stroke: "var(--pewter)", strokeWidth: "1" }), yAxisTicks.map((tick, i) => (_jsxs("g", { children: [_jsx("line", { x1: paddingLeft - 4, y1: tick.y, x2: paddingLeft, y2: tick.y, stroke: "var(--pewter)", strokeWidth: "1" }), _jsx("text", { x: paddingLeft - 6, y: tick.y, textAnchor: "end", dominantBaseline: "middle", fill: "var(--dew)", fontSize: "9", opacity: "0.7", children: i === 0 ? `${tick.label} ${tick.suffix}` : tick.label })] }, i))), segmentAreaPaths.map((areaPath, i) => (_jsx("path", { d: areaPath, fill: showValueGradient
                                    ? `url(#${valueGradientId}-fill)`
                                    : `url(#profileGradient-${color.replace('#', '')})` }, `area-${i}`))), highlightSegmentRange && (_jsxs(_Fragment, { children: [highlightSegmentRange.startFraction > 0 && (_jsx("rect", { x: paddingLeft, y: paddingTop, width: highlightSegmentRange.startFraction * graphWidth, height: graphHeight, fill: "rgba(0,0,0,0.4)" })), highlightSegmentRange.endFraction < 1 && (_jsx("rect", { x: paddingLeft + highlightSegmentRange.endFraction * graphWidth, y: paddingTop, width: (1 - highlightSegmentRange.endFraction) * graphWidth, height: graphHeight, fill: "rgba(0,0,0,0.4)" }))] })), segmentBoundaries && segmentBoundaries.map((fraction, i) => {
                                const x = paddingLeft + fraction * graphWidth;
                                return (_jsx("line", { x1: x, y1: paddingTop, x2: x, y2: paddingTop + graphHeight, stroke: "var(--dew)", strokeWidth: "1", strokeDasharray: "3,2", opacity: "0.5" }, `seg-boundary-${i}`));
                            }), segmentPaths.map((pathD, i) => (_jsx("path", { d: pathD, fill: "none", stroke: showValueGradient ? `url(#${valueGradientId})` : color, strokeWidth: "2.5", strokeLinecap: "round", strokeLinejoin: "round" }, `line-${i}`))), endpointDots.map((dot, i) => (_jsx("circle", { cx: dot.cx, cy: dot.cy, r: "3", fill: color }, `dot-${i}`))), crosshairInfo && (_jsxs(_Fragment, { children: [_jsx("line", { x1: crosshairInfo.x, y1: paddingTop, x2: crosshairInfo.x, y2: paddingTop + graphHeight, stroke: "var(--sand)", strokeWidth: "1", strokeDasharray: "3,2", opacity: "0.8" }), crosshairInfo.y !== null && (_jsx("circle", { cx: crosshairInfo.x, cy: crosshairInfo.y, r: "4", fill: color, stroke: "white", strokeWidth: "1.5" })), (() => {
                                        const elevText = crosshairInfo.elevation !== null ? formatElevation(crosshairInfo.elevation, unit) : 'N/A';
                                        let extraText = '';
                                        if ((colorBy === 'slope' || colorBy === 'slope_grade') && crosshairInfo.slopeDeg !== null) {
                                            const deg = Math.abs(crosshairInfo.slopeDeg);
                                            const grade = Math.tan(deg * Math.PI / 180) * 100;
                                            extraText = ` · ${deg.toFixed(1)}° / ${grade.toFixed(0)}%`;
                                        }
                                        else if (colorBy === 'aspect' && crosshairInfo.bearingDeg !== null) {
                                            const b = ((crosshairInfo.bearingDeg % 360) + 360) % 360;
                                            extraText = ` · ${bearingShort(b)} ${b.toFixed(0)}°`;
                                        }
                                        else if (colorBy === 'geomorphons' && crosshairInfo.geomorphonClass !== null) {
                                            extraText = ` · ${geomorphonLabel(crosshairInfo.geomorphonClass)}`;
                                        }
                                        else if (colorBy === 'twi' && crosshairInfo.twiValue !== null) {
                                            extraText = ` · TWI ${crosshairInfo.twiValue.toFixed(2)}`;
                                        }
                                        else if (colorBy === 'flow_accumulation' && crosshairInfo.flowAccValue !== null) {
                                            // Flow accumulation values are upstream-cell counts — can be
                                            // huge. Compact integer with thousands separator reads
                                            // better than scientific notation at this scale.
                                            extraText = ` · FlowAcc ${Math.round(crosshairInfo.flowAccValue).toLocaleString()}`;
                                        }
                                        const tooltipText = elevText + extraText;
                                        const tooltipWidth = Math.max(70, tooltipText.length * 5 + 10);
                                        return (_jsxs(_Fragment, { children: [_jsx("rect", { x: Math.min(crosshairInfo.x + 6, svgWidth - tooltipWidth - 5), y: crosshairInfo.y !== null ? Math.max(crosshairInfo.y - 22, paddingTop) : paddingTop + graphHeight / 2 - 9, width: tooltipWidth, height: "18", rx: "3", fill: "var(--charcoal)", opacity: "0.9" }), _jsx("text", { x: Math.min(crosshairInfo.x + 10, svgWidth - tooltipWidth - 1), y: crosshairInfo.y !== null ? Math.max(crosshairInfo.y - 10, paddingTop + 12) : paddingTop + graphHeight / 2 + 3, fill: "var(--sand)", fontSize: "8", children: tooltipText })] }));
                                    })()] })), _jsx("text", { x: paddingLeft, y: paddingTop + graphHeight + 12, textAnchor: "start", fontSize: "10", fill: "var(--dew)", opacity: "0.7", children: "0" }), _jsx("text", { x: paddingLeft + graphWidth, y: paddingTop + graphHeight + 12, textAnchor: "end", fontSize: "10", fill: "var(--dew)", opacity: "0.7", children: formatDistance(totalDistance, unit) })] }))] }), !isPlaceholder && stats && !hideMeasurementSection && (_jsx("div", { className: "mb-3", children: _jsxs("div", { className: "grid grid-cols-2 gap-x-4 text-xs items-center", style: { color: 'var(--dew)' }, children: [_jsxs("span", { children: [_jsx("span", { style: { color: 'var(--sand)' }, children: formatDistancePrecise(totalDistance3d, unit) }), " ground"] }), _jsxs("span", { className: "flex items-center gap-1", children: [_jsxs("span", { children: [_jsx("span", { style: { color: 'var(--sand)' }, children: formatDistancePrecise(totalDistance, unit) }), " linear"] }), _jsxs("span", { className: "help-tooltip-trigger", style: { cursor: 'help', opacity: 0.6, display: 'inline-flex', position: 'relative' }, children: [_jsx(HelpCircle, { size: 12 }), _jsx("span", { className: "help-tooltip", children: "Ground length follows the terrain surface accounting for elevation changes (the actual distance you would walk). Linear length is the flat 2D geodesic distance." })] })] })] }) })), !isPlaceholder && (colorBy === 'slope_grade' || colorBy === 'slope') && (() => {
                const slopeCols = containerWidth >= 740 ? 3 : containerWidth >= 480 ? 2 : 1;
                const slopeRows = Math.ceil(SLOPE_BANDS.length / slopeCols);
                return (_jsx("div", { className: "mb-3 text-[10px]", style: {
                        color: 'var(--dew)',
                        display: 'grid',
                        gridTemplateColumns: `repeat(${slopeCols}, minmax(0, 1fr))`,
                        gridTemplateRows: `repeat(${slopeRows}, auto)`,
                        gridAutoFlow: 'column',
                        columnGap: 12,
                        rowGap: 4,
                    }, children: SLOPE_BANDS.map((band) => (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { style: { display: 'inline-block', width: 10, height: 10, backgroundColor: band.color, borderRadius: 2, flexShrink: 0 } }), _jsx("span", { children: band.label })] }, band.label))) }));
            })(), !isPlaceholder && colorBy === 'aspect' && (_jsx("div", { className: "flex flex-wrap items-center gap-x-3 gap-y-1 mb-3 text-[10px]", style: { color: 'var(--dew)' }, children: aspectBands.map((band) => (_jsxs("div", { className: "flex items-center gap-1", children: [_jsx("span", { style: { display: 'inline-block', width: 10, height: 10, backgroundColor: band.color, borderRadius: 2 } }), _jsxs("span", { children: [band.short, " \u00B7 ", band.label] })] }, band.short))) })), !isPlaceholder && colorBy === 'geomorphons' && (_jsx("div", { className: "flex flex-wrap items-center gap-x-3 gap-y-1 mb-3 text-[10px]", style: { color: 'var(--dew)' }, children: GEOMORPHON_CLASSES.map((cls) => (_jsxs("div", { className: "flex items-center gap-1", children: [_jsx("span", { style: { display: 'inline-block', width: 10, height: 10, backgroundColor: cls.color, borderRadius: 2 } }), _jsx("span", { children: cls.label })] }, cls.id))) })), !isPlaceholder && colorBy === 'twi' && twiRange && (_jsxs("div", { className: "flex items-center gap-2 mb-3 text-[10px]", style: { color: 'var(--dew)' }, children: [_jsxs("span", { children: ["Drier \u00B7 ", twiRange.min.toFixed(1)] }), _jsx("span", { style: {
                            display: 'inline-block',
                            flex: '0 1 200px',
                            height: 10,
                            borderRadius: 2,
                            background: `linear-gradient(to right, ${rgbHex(TWI_LOW_RGB)}, ${rgbHex(TWI_HIGH_RGB)})`,
                        } }), _jsxs("span", { children: [twiRange.max.toFixed(1), " \u00B7 Wetter"] })] })), !isPlaceholder && colorBy === 'flow_accumulation' && flowAccRange && (_jsxs("div", { className: "flex items-center gap-2 mb-3 text-[10px]", style: { color: 'var(--dew)' }, children: [_jsxs("span", { children: ["Less flow \u00B7 ", Math.round(flowAccRange.min).toLocaleString()] }), _jsx("span", { style: {
                            display: 'inline-block',
                            flex: '0 1 200px',
                            height: 10,
                            borderRadius: 2,
                            background: `linear-gradient(to right, ${rgbHex(TWI_LOW_RGB)}, ${rgbHex(TWI_HIGH_RGB)})`,
                        } }), _jsxs("span", { children: [Math.round(flowAccRange.max).toLocaleString(), " \u00B7 More flow"] })] })), !isPlaceholder && stats && (_jsxs("div", { className: "mt-3", children: [_jsxs("button", { onClick: () => setShowStats(!showStats), className: "flex items-center justify-between w-full text-xs py-1", style: { color: 'var(--dew)' }, "aria-expanded": showStats, children: [_jsx("span", { children: "Statistics" }), showStats ? _jsx(ChevronUp, { size: 14 }) : _jsx(ChevronDown, { size: 14 })] }), showStats && (_jsxs("div", { className: "mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs", style: { maxWidth: 480 }, children: [_jsxs("div", { className: "flex justify-between", style: { color: 'var(--dew)' }, children: [_jsx("span", { children: "Min:" }), _jsx("span", { style: { color: 'var(--sand)' }, children: formatElevation(stats.min, unit) })] }), _jsxs("div", { className: "flex justify-between", style: { color: 'var(--dew)' }, children: [_jsx("span", { children: "Max:" }), _jsx("span", { style: { color: 'var(--sand)' }, children: formatElevation(stats.max, unit) })] }), _jsxs("div", { className: "flex justify-between", style: { color: 'var(--dew)' }, children: [_jsx("span", { children: "Mean:" }), _jsx("span", { style: { color: 'var(--sand)' }, children: formatElevation(stats.mean, unit) })] }), _jsxs("div", { className: "flex justify-between", style: { color: 'var(--dew)' }, children: [_jsx("span", { children: "Range:" }), _jsx("span", { style: { color: 'var(--sand)' }, children: formatElevation(stats.range, unit) })] }), _jsxs("div", { className: "flex justify-between", style: { color: 'var(--dew)' }, children: [_jsx("span", { title: "Net elevation change: end minus start (= Gain \u2212 Loss). The straight start-to-end difference, e.g. between two contours.", children: "Net \u0394:" }), _jsxs("span", { style: { color: stats.net >= 0 ? '#32CD32' : '#DC143C' }, children: [stats.net >= 0 ? '+' : '-', formatElevation(Math.abs(stats.net), unit)] })] }), _jsxs("div", { className: "flex justify-between", style: { color: 'var(--dew)' }, children: [_jsx("span", { children: "Gain/Loss:" }), _jsxs("span", { children: [_jsxs("span", { style: { color: '#32CD32' }, children: ["+", formatElevation(stats.gain, unit)] }), _jsx("span", { style: { color: 'var(--dew)' }, children: " / " }), _jsxs("span", { style: { color: '#DC143C' }, children: ["-", formatElevation(stats.loss, unit)] })] })] }), _jsxs("div", { className: "flex justify-between", style: { color: 'var(--dew)' }, children: [_jsx("span", { children: "Std Dev:" }), _jsx("span", { style: { color: 'var(--sand)' }, children: formatElevation(stats.stdev, unit) })] })] }))] }))] }));
};
